import React, { useEffect, useState } from 'react';
import { X, Loader2, Copy, RefreshCw, Send, CalendarClock, Sparkles, ChevronDown, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from './IntelligenceLayout';
import { useGraphStatus, GraphMailboxBadge, connectMicrosoftMailbox, isAdminUser } from '../components/GraphMailboxStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const MODELS = [
  { key: 'claude', label: 'Claude Sonnet 4.6' },
  { key: 'gpt-mini', label: 'GPT-5.4 Mini (fast)' },
];

// -- Schedule preset helpers (Omnia local/business time) --
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
const nextWeekday = (d) => { const nd = new Date(d); while (isWeekend(nd)) nd.setDate(nd.getDate() + 1); return nd; };
const atTime = (d, h, m) => { const nd = new Date(d); nd.setHours(h, m, 0, 0); return nd; };
const presetTomorrow9am = () => { const d = new Date(); d.setDate(d.getDate() + 1); return atTime(nextWeekday(d), 9, 0); };
const presetNextBizDay9am = () => { const d = new Date(); d.setDate(d.getDate() + 1); return atTime(nextWeekday(d), 9, 0); };
const presetNextBizDay1pm = () => { const d = new Date(); d.setDate(d.getDate() + 1); return atTime(nextWeekday(d), 13, 0); };
const toLocalDatetimeInputValue = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const postJSON = async (url, body) => {
  const token = localStorage.getItem('backoffice_token');
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
};

const CRMOutreachModal = ({ company, contact, initialDraftType = 'initial', onClose, onLogged }) => {
  const [draftType, setDraftType] = useState(initialDraftType);
  const [model, setModel] = useState('claude');
  const [draft, setDraft] = useState(null);
  const [noPriorActivity, setNoPriorActivity] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [followUpDate, setFollowUpDate] = useState(todayPlus(5));
  const [busy, setBusy] = useState(false);
  const [sendState, setSendState] = useState('idle'); // idle | sending
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [customAt, setCustomAt] = useState('');
  const { status: graphStatus, reload: reloadGraphStatus } = useGraphStatus();

  const generate = async (type = draftType, m = model) => {
    setGenerating(true);
    setNoPriorActivity(null);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/crm/companies/${company.id}/outreach-draft`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: contact.id, draft_type: type, model: m }),
      });
      if (res.no_prior_activity) {
        setDraft(null);
        setNoPriorActivity(res.detail);
      } else {
        setDraft({ subject: res.subject, body: res.body });
      }
    } catch (e) {
      toast.error('Could not generate draft — try again');
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => { generate(initialDraftType, 'claude'); }, []); // eslint-disable-line

  const switchType = (type) => { setDraftType(type); generate(type, model); };
  const switchModel = (m) => { setModel(m); generate(draftType, m); };

  const copyDraft = () => {
    navigator.clipboard.writeText(`Subject: ${draft.subject}\n\n${draft.body}`)
      .then(() => toast.success('Draft copied'))
      .catch(() => toast.error('Could not copy — select and copy manually'));
  };

  const canSend = !!draft && draftType && graphStatus.connected;

  const openSendConfirm = () => {
    if (!canSend || sendState !== 'idle') return;
    setShowSendConfirm(true);
  };

  const confirmAndSendNow = async () => {
    if (!canSend || sendState !== 'idle') return;
    setSendState('sending');
    try {
      const data = await postJSON(`${API_URL}/api/crm/companies/${company.id}/outreach-send`, {
        contact_id: contact.id, subject: draft.subject, body: draft.body, draft_type: draftType,
      });
      toast.success(data.message || `Sent to ${contact.email}`);
      setShowSendConfirm(false);
      onLogged && onLogged();
      onClose();
    } catch (e) {
      setSendState('idle');
      toast.error(e.message || 'Send failed');
    }
  };

  const scheduleSend = async (dateObj) => {
    if (!canSend) return;
    if (dateObj.getTime() <= Date.now()) {
      toast.error('Scheduled time must be in the future');
      return;
    }
    setScheduling(true);
    try {
      const data = await postJSON(`${API_URL}/api/crm/companies/${company.id}/outreach-schedule`, {
        contact_id: contact.id, subject: draft.subject, body: draft.body, draft_type: draftType,
        scheduled_at: dateObj.toISOString(),
      });
      toast.success(`Scheduled for ${dateObj.toLocaleString()}`);
      onLogged && onLogged();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Could not schedule');
    } finally {
      setScheduling(false);
    }
  };

  const connectAndRefresh = async () => { await connectMicrosoftMailbox(); };

  const logAsSent = async () => {
    setBusy(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/companies/${company.id}/activities`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: `Outreach sent (${draftType === 'follow_up' ? 'follow-up' : 'initial'}) — ${contact.name || contact.first_name}`,
          notes: `Subject: ${draft.subject}\n\n${draft.body}`,
          activity_type: 'outreach',
        }),
      });
      await fetchWithAuth(`${API_URL}/api/crm/companies/${company.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pipeline_stage: 'Outreach', next_action: 'Follow up on outreach', next_action_due_date: followUpDate }),
      });
      toast.success(`${company.name} moved to Awaiting Response`);
      onLogged && onLogged();
      onClose();
    } catch (e) {
      toast.error('Could not log outreach');
    } finally {
      setBusy(false);
    }
  };

  const setFollowUpOnly = async () => {
    setBusy(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/companies/${company.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ next_action: 'Follow up', next_action_due_date: followUpDate }),
      });
      await fetchWithAuth(`${API_URL}/api/crm/companies/${company.id}/activities`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: `Follow-up set for ${followUpDate}`, activity_type: 'outreach' }),
      });
      toast.success('Follow-up scheduled');
      onLogged && onLogged();
    } catch (e) {
      toast.error('Could not set follow-up');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4" data-testid="outreach-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#0A1F44] flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-[#D4AF37]" /> Prepare Outreach</h3>
            <p className="text-xs text-gray-500 mt-0.5">{company.name} — {contact.name || `${contact.first_name} ${contact.last_name || ''}`}{contact.title ? ` (${contact.title})` : ''}</p>
          </div>
          <button onClick={onClose} data-testid="close-outreach-modal"><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        <GraphMailboxBadge status={graphStatus} onConnect={connectAndRefresh} />

        <div className="flex gap-2">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1" data-testid="outreach-type-toggle">
            <button onClick={() => switchType('initial')} data-testid="outreach-type-initial"
              className={`px-2.5 py-1 rounded-md text-xs font-medium ${draftType === 'initial' ? 'bg-white text-[#0A1F44] shadow-sm' : 'text-gray-500'}`}>Initial outreach</button>
            <button onClick={() => switchType('follow_up')} data-testid="outreach-type-followup"
              className={`px-2.5 py-1 rounded-md text-xs font-medium ${draftType === 'follow_up' ? 'bg-white text-[#0A1F44] shadow-sm' : 'text-gray-500'}`}>Short follow-up</button>
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1" data-testid="outreach-model-toggle">
            {MODELS.map(m => (
              <button key={m.key} onClick={() => switchModel(m.key)} data-testid={`outreach-model-${m.key}`}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap ${model === m.key ? 'bg-white text-[#0A1F44] shadow-sm' : 'text-gray-500'}`}>{m.label}</button>
            ))}
          </div>
        </div>

        {generating ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : noPriorActivity ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="outreach-no-prior-activity">
            {noPriorActivity}
            <button onClick={() => switchType('initial')} data-testid="outreach-switch-to-initial-btn"
              className="mt-2 text-xs font-medium text-[#0A1F44] underline">Switch to Initial outreach</button>
          </div>
        ) : draft ? (
          <div className="space-y-2">
            <input value={draft.subject} onChange={e => setDraft({ ...draft, subject: e.target.value })} data-testid="outreach-subject-input"
              className="w-full text-sm font-medium px-3 py-2 border border-gray-200 rounded-lg" placeholder="Subject" />
            <textarea value={draft.body} onChange={e => setDraft({ ...draft, body: e.target.value })} data-testid="outreach-body-input" rows={8}
              className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg resize-none" placeholder="Message body" />
          </div>
        ) : null}

        <div className="flex gap-2">
          <button onClick={copyDraft} disabled={!draft} data-testid="outreach-copy-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:border-[#0A1F44]/40 disabled:opacity-40">
            <Copy className="h-3.5 w-3.5" /> Copy Draft
          </button>
          <button onClick={() => generate()} disabled={generating} data-testid="outreach-regenerate-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:border-[#0A1F44]/40 disabled:opacity-40">
            <RefreshCw className="h-3.5 w-3.5" /> Regenerate
          </button>
        </div>

        {/* Primary human-reviewed send path (Phase 6E) */}
        <div className="pt-3 border-t border-gray-100 space-y-2">
          <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Send via Outlook</label>
          <div className="flex gap-2">
            <button onClick={openSendConfirm} disabled={!canSend || sendState !== 'idle'} data-testid="outreach-send-now-btn"
              title={!graphStatus.connected ? 'Connect the Outlook mailbox first' : ''}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-40">
              {sendState === 'sending' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {sendState === 'sending' ? 'Sending…' : 'Send Now'}
            </button>
            <div className="relative flex-1">
              <button onClick={() => setScheduleOpen(o => !o)} disabled={!canSend} data-testid="outreach-schedule-toggle-btn"
                title={!graphStatus.connected ? 'Connect the Outlook mailbox first' : ''}
                className="w-full flex items-center justify-center gap-1.5 py-2 border border-[#0A1F44]/30 text-[#0A1F44] rounded-lg text-sm font-medium hover:bg-[#fdf9ef] disabled:opacity-40">
                <CalendarClock className="h-3.5 w-3.5" /> Schedule Send <ChevronDown className="h-3 w-3" />
              </button>
              {scheduleOpen && (
                <div className="absolute right-0 bottom-full mb-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl p-2 z-10" data-testid="outreach-schedule-panel">
                  {[
                    { key: 'tomorrow', label: 'Tomorrow, 9:00 AM', get: presetTomorrow9am },
                    { key: 'nbd9', label: 'Next Business Day, 9:00 AM', get: presetNextBizDay9am },
                    { key: 'nbd1', label: 'Next Business Day, 1:00 PM', get: presetNextBizDay1pm },
                  ].map(p => (
                    <button key={p.key} onClick={() => scheduleSend(p.get())} disabled={scheduling} data-testid={`outreach-schedule-preset-${p.key}`}
                      className="w-full text-left text-xs px-2.5 py-2 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50">
                      {p.label}
                    </button>
                  ))}
                  <div className="border-t border-gray-100 mt-1 pt-2 px-1 space-y-1.5">
                    <label className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Custom date &amp; time</label>
                    <input type="datetime-local" value={customAt} min={toLocalDatetimeInputValue(new Date())}
                      onChange={e => setCustomAt(e.target.value)} data-testid="outreach-schedule-custom-input"
                      className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" />
                    <button onClick={() => customAt && scheduleSend(new Date(customAt))} disabled={!customAt || scheduling} data-testid="outreach-schedule-custom-confirm-btn"
                      className="w-full py-1.5 bg-[#0A1F44] text-white rounded-lg text-xs font-medium disabled:opacity-40">
                      {scheduling ? 'Scheduling…' : 'Schedule'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {!graphStatus.connected && graphStatus.configured && isAdminUser() && (
            <button onClick={connectAndRefresh} data-testid="outreach-connect-inline-btn" className="text-[11px] text-[#0A1F44] underline flex items-center gap-1">
              <Link2 className="h-3 w-3" /> Connect Outlook mailbox to enable sending
            </button>
          )}
        </div>

        <div className="pt-3 border-t border-gray-100 space-y-2">
          <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Follow-up date (manual actions below)</label>
          <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} data-testid="outreach-followup-date"
            className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded-lg" />
          <div className="flex gap-2">
            <button onClick={logAsSent} disabled={!draft || busy} data-testid="outreach-log-sent-btn"
              className="flex-1 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:border-[#0A1F44]/40 disabled:opacity-50">
              Log as Sent Manually
            </button>
            <button onClick={setFollowUpOnly} disabled={busy} data-testid="outreach-set-followup-btn"
              className="flex-1 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:border-[#0A1F44]/40 disabled:opacity-50">
              Set Follow-Up Only
            </button>
          </div>
          <p className="text-[10px] text-gray-400">Use "Log as Sent Manually" only if this message was already sent outside Omnia (e.g. a personal email client) — it does not send anything.</p>
        </div>
      </div>

      {showSendConfirm && draft && (
        <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4" data-testid="outreach-send-confirm-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h4 className="text-base font-bold text-[#0A1F44] flex items-center gap-1.5"><Send className="h-4 w-4 text-[#D4AF37]" /> Confirm Send</h4>
            <p className="text-xs text-gray-500">This will send a real email via Outlook right now. Review before confirming.</p>
            <div className="space-y-2 text-sm bg-gray-50 border border-gray-100 rounded-lg p-3">
              <div><span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">To</span><p className="text-[#0A1F44] font-medium" data-testid="outreach-send-confirm-to">{contact.email}</p></div>
              <div><span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">From</span><p className="text-[#0A1F44] font-medium" data-testid="outreach-send-confirm-from">{graphStatus.mailbox || '—'}</p></div>
              <div><span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">Subject</span><p className="text-[#0A1F44] font-medium" data-testid="outreach-send-confirm-subject">{draft.subject}</p></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowSendConfirm(false)} disabled={sendState !== 'idle'} data-testid="outreach-send-confirm-cancel-btn"
                className="flex-1 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:border-[#0A1F44]/40 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={confirmAndSendNow} disabled={sendState !== 'idle'} data-testid="outreach-send-confirm-confirm-btn"
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
                {sendState === 'sending' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {sendState === 'sending' ? 'Sending…' : 'Confirm Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMOutreachModal;
